<template>
  <div class="space-y-6">
    <!-- Estado y Prioridad -->
    <div class="card">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold text-gray-900">Estado del Ticket</h3>
        <button
          @click="$emit('edit-ticket')"
          class="px-3 py-1 bg-primary text-white text-sm rounded-lg hover:bg-primary-dark transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 flex items-center gap-1"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
          </svg>
          Editar
        </button>
      </div>
      
      <div class="space-y-4">
        <!-- Estado -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Estado Actual</label>
          <div class="flex items-center space-x-2">
            <span :class="statusInfo.color" class="px-3 py-1 rounded-full text-sm font-medium">
              {{ statusInfo.label }}
            </span>
            <div :class="statusInfo.badgeColor" class="w-3 h-3 rounded-full"></div>
          </div>
        </div>

        <!-- Prioridad -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Prioridad</label>
          <span :class="priorityInfo.color" class="px-3 py-1 rounded text-xs font-semibold text-white">
            {{ priorityInfo.label }}
          </span>
        </div>

        <!-- SLA Progress -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Progreso SLA</label>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div 
              class="h-2 rounded-full transition-all duration-300"
              :class="getSLAColor(ticket.slaPercent)"
              :style="{ width: `${ticket.slaPercent}%` }"
            ></div>
          </div>
          <span class="text-xs text-gray-500 mt-1">{{ ticket.slaPercent }}% completado</span>
        </div>
      </div>
    </div>

    <!-- Información del Cliente -->
    <div class="card">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Información del Cliente</h3>
      
      <div class="space-y-3">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 bg-ecomac-primary rounded-full flex items-center justify-center text-white font-semibold">
            {{ ticket.client.avatar }}
          </div>
          <div>
            <p class="font-medium text-gray-900">{{ ticket.client.name }}</p>
            <p class="text-sm text-gray-500">{{ ticket.client.email }}</p>
          </div>
        </div>
        
        <div>
          <p class="text-sm font-medium text-gray-700">Empresa</p>
          <p class="text-sm text-gray-900">{{ ticket.client.company }}</p>
        </div>
      </div>
    </div>

    <!-- Asignación -->
    <div class="card">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Asignación</h3>
      
      <div class="space-y-3">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
            {{ ticket.assignedTo?.avatar || ticket.assignedTo?.name?.substring(0, 2) || 'EA' }}
          </div>
          <div>
            <p class="font-medium text-gray-900">{{ ticket.assignedTo?.name || ticket.assignedTo || 'Equipo de Soporte' }}</p>
            <p class="text-sm text-gray-500">{{ ticket.assignedTo?.role || 'Agente' }}</p>
          </div>
        </div>
        
        <div v-if="ticket.assignedTo?.email">
          <p class="text-sm text-gray-500">{{ ticket.assignedTo.email }}</p>
        </div>
      </div>
    </div>

    <!-- Fechas -->
    <div class="card">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Fechas Importantes</h3>
      
      <div class="space-y-3">
        <div>
          <p class="text-sm font-medium text-gray-700">Fecha de Creación</p>
          <p class="text-sm text-gray-900">{{ formatDate(ticket.createdAt) }}</p>
        </div>
        
        <div>
          <p class="text-sm font-medium text-gray-700">Última Actualización</p>
          <p class="text-sm text-gray-900">{{ formatDate(ticket.updatedAt) }}</p>
        </div>
        
        <div v-if="ticket.dueDate">
          <p class="text-sm font-medium text-gray-700">Fecha Límite</p>
          <p class="text-sm text-gray-900">{{ formatDate(ticket.dueDate) }}</p>
        </div>
      </div>
    </div>

    <!-- Detalles Adicionales -->
    <div class="card">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Detalles Adicionales</h3>
      
      <div class="space-y-3">
        <div>
          <p class="text-sm font-medium text-gray-700">Categoría</p>
          <p class="text-sm text-gray-900">{{ ticket.category }}</p>
        </div>
        
        <div v-if="ticket.customFields?.environment">
          <p class="text-sm font-medium text-gray-700">Ambiente</p>
          <p class="text-sm text-gray-900">{{ ticket.customFields.environment }}</p>
        </div>
        
        <div v-if="ticket.customFields?.affectedUsers">
          <p class="text-sm font-medium text-gray-700">Usuarios Afectados</p>
          <p class="text-sm text-gray-900">{{ ticket.customFields.affectedUsers }}</p>
        </div>
        
        <div v-if="ticket.customFields?.severity">
          <p class="text-sm font-medium text-gray-700">Severidad</p>
          <p class="text-sm text-gray-900">{{ ticket.customFields.severity }}</p>
        </div>
      </div>
    </div>

    <!-- Tags -->
    <div class="card">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Tags</h3>
      
      <div v-if="ticket.tags && ticket.tags.length > 0" class="flex flex-wrap gap-2">
        <span 
          v-for="tag in ticket.tags" 
          :key="tag"
          class="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md"
        >
          #{{ tag }}
        </span>
      </div>
      <p v-else class="text-sm text-gray-500">Sin tags asignados</p>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
// TODO: Reemplazar con servicios reales cuando estén disponibles
function getStatusInfo(status) {
  const statusMap = {
    'open': { label: 'Abierto', color: 'bg-red-100 text-red-800', badgeColor: 'bg-red-500' },
    'in_progress': { label: 'En Progreso', color: 'bg-yellow-100 text-yellow-800', badgeColor: 'bg-yellow-500' },
    'resolved': { label: 'Resuelto', color: 'bg-green-100 text-green-800', badgeColor: 'bg-green-500' },
    'closed': { label: 'Cerrado', color: 'bg-gray-100 text-gray-800', badgeColor: 'bg-gray-500' }
  };
  return statusMap[status] || statusMap['open'];
}

function getPriorityInfo(priority) {
  const priorityMap = {
    'baja': { label: 'Baja', color: 'bg-green-500', textColor: 'text-green-700' },
    'media': { label: 'Media', color: 'bg-yellow-500', textColor: 'text-yellow-700' },
    'alta': { label: 'Alta', color: 'bg-red-500', textColor: 'text-red-700' }
  };
  return priorityMap[priority] || priorityMap['media'];
}

function formatDate(timestamp) {
  return new Date(timestamp).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

const props = defineProps({
  ticket: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['edit-ticket']);

const statusInfo = computed(() => getStatusInfo(props.ticket.status));
const priorityInfo = computed(() => getPriorityInfo(props.ticket.priority));

function getSLAColor(percent) {
  if (percent >= 80) return 'bg-green-500';
  if (percent >= 60) return 'bg-yellow-500';
  if (percent >= 40) return 'bg-orange-500';
  return 'bg-red-500';
}
</script>

<style scoped>
.card {
  @apply bg-white rounded-lg shadow-sm border border-gray-200 p-4;
}
</style>
